import type { Editor } from '@tiptap/react';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';

import { Button } from '@/ui/Button';
import { Icon, IconName } from '@/ui/Icon';
import { Input } from '@/ui/Input';
import { Label } from '@/ui/Label';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/Popover';
import { useTranslation } from 'react-i18next';

interface IEditorAddLink {
	editor: Editor;
}

const EditorAddLink = ({ editor }: IEditorAddLink) => {
	// NOTE: Backup link value for reset
	const [href, setHref] = useState('');

	// NOTE: Backup text value
	const [selected, setSelected] = useState('');

	const [link, setLink] = useState('');
	const [text, setText] = useState('');

	const handleConfirm = useCallback(() => {
		const { view } = editor;
		const { from, to } = view.state.selection;

		if (from === to) {
			editor?.commands.insertContent(text);
			editor?.commands.setTextSelection({ from, to: to + text.length });
		}

		if (!link) {
			editor?.chain().focus().extendMarkRange('link').unsetLink().run();

			return;
		}

		editor?.chain().focus().extendMarkRange('link').setLink({ href: link }).run();
	}, [editor, link, text]);

	const init = useCallback(() => {
		editor?.commands.extendMarkRange('link');

		const { view, state } = editor;
		const { from, to } = view.state.selection;

		const href = (editor?.getAttributes('link').href as string) ?? '';
		const selected = state.doc.textBetween(from, to, '\n') ?? '';

		setHref(href);
		setSelected(selected);

		setLink(href);
		setText(selected);
	}, [editor]);

	if (!editor) {
		return null;
	}

	const { t } = useTranslation();

	return (
		<Popover modal onOpenChange={init}>
			<PopoverTrigger asChild>
				<Button
					className={clsx('w-6 h-6 text-brand-gray-2 [&>svg]:size-6 [&>svg]:reset p-0 [&>svg]:left-[0px]', {
						'text-brand-blue': editor.isActive('link'),
					})}
					size="icon"
					icon="left"
					variant="tetriary"
				>
					<Icon name="link" size={24} />
				</Button>
			</PopoverTrigger>

			<PopoverContent className="w-96 p-0" align="center">
				<div className="p-4 flex flex-col gap-2">
					<div className="px-1 py-1">
						<Label htmlFor="email">{t('editor.textLink')}</Label>

						<Input
							placeholder={t('editor.textLink') as string}
							onChange={(event) => setText(event.currentTarget.value)}
							value={text}
							disabled={!!selected}
						/>
					</div>

					<div className="px-1 py-1">
						<Label htmlFor="email">{t('editor.link')}</Label>

						<Input
							placeholder="http://"
							type="url"
							onChange={(event) => setLink(event.currentTarget.value)}
							value={link}
						/>
					</div>

					<div className="flex px-1 py-1">
						<Button
							variant="danger"
							icon="left"
							size="sm"
							onClick={() => setLink('')}
							title={t('editor.removeLink') as string}
							disabled={!link}
						>
							<Icon name="trash" />
							{t('common.del')}
						</Button>

						<Button
							variant="tetriary"
							size="sm"
							onClick={() => setLink(href)}
							title={t('editor.resetLink') as string}
							disabled={href === link}
							className="m-auto"
						>
							{t('buttons.reset1')}
						</Button>

						<Button
							size="sm"
							variant="default"
							icon="left"
							onClick={handleConfirm}
							disabled={href === link}
							title={t('common.save') as string}
						>
							<Icon name={IconName.link} />
							{t('common.save')}
						</Button>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
};

export { EditorAddLink };
